import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HDate } from '@hebcal/core';
import PrimaryButton from '../components/PrimaryButton';
import BottomNav from '../components/BottomNav';
import OnboardingProgressHeader from '../components/OnboardingProgressHeader';
import { computeBarMitzvahData, formatCivil, formatHebrew } from '../utils/parashaCalc';
import { setParasha, setAliyaLatoraDate } from '../utils/userSelections';
import { mapHebcalParasha } from '../utils/parashaNameMap';
import ParashaSelector from '../components/ParashaSelector';
import parashaList from '../../parasha_map.json';

type OptionItem = { value: number; label: string | number };

const MONTH_KEYS = [
  'month_january',
  'month_february',
  'month_march',
  'month_april',
  'month_may',
  'month_june',
  'month_july',
  'month_august',
  'month_september',
  'month_october',
  'month_november',
  'month_december',
];

const YEARS = (() => {
  const current = new Date().getFullYear();
  const start = current - 100;
  const end = current + 2;
  const years: number[] = [];
  for (let y = end; y >= start; y -= 1) {
    years.push(y);
  }
  return years;
})();

const daysInMonth = (year: number, monthIndex: number) =>
  new Date(year, monthIndex + 1, 0).getDate();

const formatDateInput = (day: number, monthIndex: number, year: number) => {
  const dd = String(day).padStart(2, '0');
  const mm = String(monthIndex + 1).padStart(2, '0');
  return `${dd}/${mm}/${year}`;
};

const HEBREW_LETTERS = {
  ones: ['', 'א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט'],
  tens: ['', 'י', 'כ', 'ל', 'מ', 'נ', 'ס', 'ע', 'פ', 'צ'],
  hundreds: ['', 'ק', 'ר', 'ש', 'ת', 'תק', 'תר', 'תש', 'תת', 'תתק'],
} as const;

const toHebrewNumber = (num: number): string => {
  if (num <= 0) return '';
  let n = num;
  const parts: string[] = [];

  const thousands = Math.floor(n / 1000);
  if (thousands) {
    parts.push(toHebrewNumber(thousands));
    n %= 1000;
  }

  const hundreds = Math.floor(n / 100);
  if (hundreds) {
    parts.push(HEBREW_LETTERS.hundreds[hundreds]);
    n %= 100;
  }

  if (n === 15) {
    parts.push('טו');
    return parts.join('');
  }
  if (n === 16) {
    parts.push('טז');
    return parts.join('');
  }

  const tens = Math.floor(n / 10);
  const ones = n % 10;
  if (tens) parts.push(HEBREW_LETTERS.tens[tens]);
  if (ones) parts.push(HEBREW_LETTERS.ones[ones]);

  return parts.join('');
};

const HEBREW_MONTH_LABELS: Record<number, string> = {
  7: 'תשרי',
  8: 'חשוון',
  9: 'כסלו',
  10: 'טבת',
  11: 'שבט',
  12: 'אדר',
  13: 'אדר ב׳',
  1: 'ניסן',
  2: 'אייר',
  3: 'סיוון',
  4: 'תמוז',
  5: 'אב',
  6: 'אלול',
};

type Parasha = { english: string; hebrew: string };
const ALL_PARASHOT = parashaList as Parasha[];

const HEB_YEARS = (() => {
  const current = new HDate().getFullYear();
  const start = current - 20;
  const end = current + 2;
  const years: number[] = [];
  for (let y = end; y >= start; y -= 1) {
    years.push(y);
  }
  return years;
})();

const getHebrewMonthsForYear = (year: number): OptionItem[] => {
  const isLeap = new HDate(1, 7, year).isLeapYear();
  const months: { value: number; label: string }[] = [
    { value: 7, label: HEBREW_MONTH_LABELS[7] },
    { value: 8, label: HEBREW_MONTH_LABELS[8] },
    { value: 9, label: HEBREW_MONTH_LABELS[9] },
    { value: 10, label: HEBREW_MONTH_LABELS[10] },
    { value: 11, label: HEBREW_MONTH_LABELS[11] },
    {
      value: 12,
      label: isLeap ? 'אדר א׳' : HEBREW_MONTH_LABELS[12],
    },
  ];

  if (isLeap) {
    months.push({ value: 13, label: HEBREW_MONTH_LABELS[13] });
  }

  months.push(
    { value: 1, label: HEBREW_MONTH_LABELS[1] },
    { value: 2, label: HEBREW_MONTH_LABELS[2] },
    { value: 3, label: HEBREW_MONTH_LABELS[3] },
    { value: 4, label: HEBREW_MONTH_LABELS[4] },
    { value: 5, label: HEBREW_MONTH_LABELS[5] },
    { value: 6, label: HEBREW_MONTH_LABELS[6] },
  );

  return months;
};

const ChooseDatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const defaultDate = { year: 2010, month: 11, day: 22 }; // Matches the provided mock
  const defaultHebrew = useMemo(
    () => new HDate(new Date(defaultDate.year, defaultDate.month, defaultDate.day)),
    [defaultDate.year, defaultDate.month, defaultDate.day],
  );

  const [calendarType, setCalendarType] = useState<'greg' | 'heb'>('greg');
  const [year, setYear] = useState<number>(defaultDate.year);
  const [month, setMonth] = useState<number>(defaultDate.month);
  const [day, setDay] = useState<number>(defaultDate.day);
  const [hebYear, setHebYear] = useState<number>(defaultHebrew.getFullYear());
  const [hebMonth, setHebMonth] = useState<number>(defaultHebrew.getMonth());
  const [hebDay, setHebDay] = useState<number>(defaultHebrew.getDate());

  const safeDay = useMemo(() => {
    const max = daysInMonth(year, month);
    return Math.min(day, max);
  }, [day, month, year]);

  const hebrewMonths = useMemo(() => getHebrewMonthsForYear(hebYear), [hebYear]);
  const activeHebMonth = useMemo(() => {
    const match = hebrewMonths.find((m) => m.value === hebMonth);
    return match ? hebMonth : (hebrewMonths[0]?.value ?? 7);
  }, [hebMonth, hebrewMonths]);

  useEffect(() => {
    if (!hebrewMonths.find((m) => m.value === hebMonth) && hebrewMonths[0]) {
      setHebMonth(hebrewMonths[0].value);
    }
  }, [hebMonth, hebrewMonths]);

  const hebDaysInMonth = useMemo(
    () => new HDate(1, activeHebMonth, hebYear).daysInMonth(),
    [activeHebMonth, hebYear],
  );

  const hebSafeDay = useMemo(() => Math.min(hebDay, hebDaysInMonth), [hebDay, hebDaysInMonth]);

  useEffect(() => {
    if (hebDay > hebDaysInMonth) setHebDay(hebDaysInMonth);
  }, [hebDay, hebDaysInMonth]);

  const dateString = useMemo(() => {
    if (calendarType === 'heb') {
      const greg = new HDate(hebSafeDay, activeHebMonth, hebYear).greg();
      return formatDateInput(greg.getDate(), greg.getMonth(), greg.getFullYear());
    }
    return formatDateInput(safeDay, month, year);
  }, [activeHebMonth, calendarType, hebSafeDay, hebYear, month, safeDay, year]);

  const result = useMemo(
    () =>
      computeBarMitzvahData(dateString, {
        israelSchedule: true,
      }),
    [dateString],
  );

  const parashaSelection = useMemo(() => mapHebcalParasha(result?.parshiot?.[0]), [result]);

  const [selectedParashaOverride, setSelectedParashaOverride] = useState<Parasha | null>(null);

  useEffect(() => {
    setSelectedParashaOverride(null);
  }, [parashaSelection?.hebrew, parashaSelection?.english]);

  const displayParasha = selectedParashaOverride ?? parashaSelection ?? null;

  const handleContinue = () => {
    if (displayParasha) {
      setParasha({
        english: displayParasha.english,
        hebrew: displayParasha.hebrew,
      });
    }
    // Save the Bar Mitzvah Shabbat date as the Aliya LaTorah date
    if (result?.shabbat) {
      const shabbat = result.shabbat;
      const dd = String(shabbat.getDate()).padStart(2, '0');
      const mm = String(shabbat.getMonth() + 1).padStart(2, '0');
      const yyyy = shabbat.getFullYear();
      setAliyaLatoraDate(`${dd}/${mm}/${yyyy}`);
    }
    navigate('/tradition');
  };

  const handleSkip = () => navigate('/tradition');

  const renderColumn = (
    items: OptionItem[],
    activeValue: number,
    onSelect: (v: number) => void,
    label?: string,
  ) => (
    <div
      style={{
        flex: 1,
        background: 'white',
        borderRadius: 18,
        padding: '8px 6px',
        boxShadow: '0 6px 22px rgba(15, 38, 74, 0.08)',
        border: '1px solid #E8ECF3',
        minWidth: 0,
      }}
    >
      {label && (
        <div
          style={{
            textAlign: 'center',
            fontSize: 12,
            color: '#7B8799',
            marginBottom: 8,
            fontWeight: 600,
          }}
        >
          {label}
        </div>
      )}
      <div
        style={{
          maxHeight: 120,
          overflowY: 'auto',
          paddingRight: 6,
          direction: 'rtl',
        }}
      >
        {items.map((item) => {
          const isActive = item.value === activeValue;
          return (
            <button
              key={item.value}
              onClick={() => onSelect(item.value)}
              style={{
                width: '100%',
                padding: '10px 8px',
                borderRadius: 12,
                border: 'none',
                background: isActive ? '#132A49' : 'transparent',
                color: isActive ? '#F8E5A9' : '#132A49',
                fontWeight: isActive ? 700 : 600,
                fontSize: 16,
                cursor: 'pointer',
                marginBottom: 6,
                transition: 'all 0.2s ease',
              }}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div
      dir="rtl"
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        color: '#0F264A',
        fontFamily: "var(--kol-font-sans, 'Heebo', sans-serif)",
      }}
    >
      <OnboardingProgressHeader step={2} />
      <div
        style={{
          width: '100%',
          maxWidth: 520,
          padding: '10px 20px 0',
          overflowY: 'auto',
        }}
      >
        <p
          style={{
            textAlign: 'center',
            fontSize: 18,
            fontWeight: 700,
            color: '#0F264A',
            marginBottom: 14,
            marginTop: 0,
          }}
        >
          {t('enter_birthdate_title')}
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 0,
            borderRadius: 16,
            overflow: 'hidden',
            border: '1px solid #132A49',
            marginBottom: 16,
          }}
        >
          <button
            onClick={() => {
              if (calendarType === 'heb') return;
              const currentCivil = new Date(year, month, safeDay);
              const h = new HDate(currentCivil);
              setHebYear(h.getFullYear());
              setHebMonth(h.getMonth());
              setHebDay(h.getDate());
              setCalendarType('heb');
            }}
            style={{
              padding: '12px 0',
              border: 'none',
              background: calendarType === 'heb' ? '#132A49' : 'white',
              color: calendarType === 'heb' ? 'white' : '#132A49',
              fontWeight: 700,
              fontSize: 16,
              cursor: 'pointer',
            }}
          >
            {t('choose_date_hebrew_calendar')}
          </button>
          <button
            onClick={() => {
              if (calendarType === 'greg') return;
              const greg = new HDate(hebSafeDay, activeHebMonth, hebYear).greg();
              setYear(greg.getFullYear());
              setMonth(greg.getMonth());
              setDay(greg.getDate());
              setCalendarType('greg');
            }}
            style={{
              padding: '12px 0',
              border: 'none',
              background: calendarType === 'greg' ? '#132A49' : 'white',
              color: calendarType === 'greg' ? 'white' : '#132A49',
              fontWeight: 700,
              fontSize: 16,
              cursor: 'pointer',
            }}
          >
            {t('choose_date_gregorian_calendar')}
          </button>
        </div>

        {calendarType === 'greg' ? (
          <div
            style={{
              display: 'flex',
              gap: 10,
              marginBottom: 20,
              alignItems: 'stretch',
            }}
          >
            {renderColumn(
              YEARS.map((y) => ({ value: y, label: y })),
              year,
              (y: number) => setYear(y),
              t('choose_date_year') as string,
            )}
            {renderColumn(
              MONTH_KEYS.map((key, idx) => ({
                value: idx,
                label: t(key) as string,
              })),
              month,
              (m: number) => setMonth(m),
              t('choose_date_month') as string,
            )}
            {renderColumn(
              Array.from({ length: daysInMonth(year, month) }, (_, i) => ({
                value: i + 1,
                label: i + 1,
              })),
              safeDay,
              (d: number) => setDay(d),
              t('choose_date_day') as string,
            )}
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              gap: 10,
              marginBottom: 20,
              alignItems: 'stretch',
            }}
          >
            {renderColumn(
              HEB_YEARS.map((y) => ({ value: y, label: toHebrewNumber(y) })),
              hebYear,
              (y: number) => setHebYear(y),
              t('choose_date_year') as string,
            )}
            {renderColumn(
              hebrewMonths,
              activeHebMonth,
              (m: number) => setHebMonth(m),
              t('choose_date_month') as string,
            )}
            {renderColumn(
              Array.from({ length: hebDaysInMonth }, (_, i) => ({
                value: i + 1,
                label: toHebrewNumber(i + 1),
              })),
              hebSafeDay,
              (d: number) => setHebDay(d),
              t('choose_date_day') as string,
            )}
          </div>
        )}

        <div
          style={{
            background: 'white',
            borderRadius: 18,
            padding: '18px 16px',
            border: '1px solid #E8ECF3',
            boxShadow: '0 6px 22px rgba(15, 38, 74, 0.08)',
            marginBottom: 16,
          }}
        >
          <ParashaSelector
            parashot={ALL_PARASHOT}
            selected={displayParasha}
            onSelect={setSelectedParashaOverride}
            label={t('choose_date_your_parasha')}
            noParashaYetText={
              result && result.parshiot.length === 0 && !selectedParashaOverride
                ? t('choose_date_special_holiday_reading')
                : t('choose_date_no_parasha_yet')
            }
            chooseAnotherHint={t('choose_date_choose_another_parasha')}
          />
          {result && (
            <div style={{ color: '#C2A32B', fontSize: 14, fontWeight: 600 }}>
              {formatCivil(result?.shabbat)}
              {' · '}
              {formatHebrew(result?.hebBM)}
            </div>
          )}
        </div>

        {result && result.parshiot.length === 0 && !selectedParashaOverride && (
          <p
            style={{
              textAlign: 'center',
              color: '#B45309',
              fontSize: 14,
              fontWeight: 600,
              marginBottom: 8,
              marginTop: 0,
            }}
          >
            {t('choose_date_holiday_not_supported')}
          </p>
        )}
        <PrimaryButton
          onClick={handleContinue}
          text={t('continue')}
          variant="gold"
          disabled={!!(result && result.parshiot.length === 0 && !selectedParashaOverride)}
          style={{ fontWeight: 700, fontSize: 18 }}
        />

        <button
          type="button"
          onClick={handleSkip}
          style={{
            display: 'block',
            margin: '12px auto 0',
            background: 'transparent',
            border: 'none',
            color: '#132A49',
            fontWeight: 400,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          {t('choose_date_skip')}
        </button>
      </div>
      <div style={{ marginTop: 'auto', width: '100%' }}>
        <BottomNav />
      </div>
    </div>
  );
};

export default ChooseDatePage;
